/**
 * React Router v4/v5 compatibility: withRouter HOC for legacy class components.
 *
 * Import withRouter from this module only. Use react-router-dom for Link, hooks, etc.
 * TypeScript types (RouteComponentProps, CompatMatch, …) live in routerCompat.d.ts.
 */
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useBlockerContext } from "containers/App/RouteCompat";

/**
 * withRouter - Compatibility HOC for React Router 7
 *
 * In React Router v4/v5, withRouter provided match, location, and history props.
 * In React Router v7, these are accessed via hooks. This HOC bridges the gap.
 *
 * @param {React.ComponentType} WrappedComponent - Component to wrap
 * @returns {React.ComponentType} - Component with router props injected
 */
export function withRouter(WrappedComponent) {
  function WithRouterWrapper(props) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const blockerContext = useBlockerContext();

    // Create a history-like object for backwards compatibility
    const history = {
      push: (pathOrLocation, state) => {
        // Handle both history.push('/path', state) and history.push({ pathname, state })
        if (typeof pathOrLocation === "object" && pathOrLocation !== null) {
          const { pathname, search, hash, state: locationState } = pathOrLocation;
          const path = pathname + (search || "") + (hash || "");
          navigate(path, { state: locationState });
        } else {
          navigate(pathOrLocation, { state });
        }
      },
      replace: (pathOrLocation, state) => {
        // Handle both history.replace('/path', state) and history.replace({ pathname, state })
        if (typeof pathOrLocation === "object" && pathOrLocation !== null) {
          const { pathname, search, hash, state: locationState } = pathOrLocation;
          const path = pathname + (search || "") + (hash || "");
          navigate(path, { replace: true, state: locationState });
        } else {
          navigate(pathOrLocation, { replace: true, state });
        }
      },
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      go: (n) => navigate(n),
      block: blockerContext?.block || (() => () => {}),
      location,
      length: window.history.length,
      action: "PUSH", // Default, not fully accurate but provides compatibility
    };

    // Create match object for backwards compatibility
    const match = {
      params,
      path: location.pathname,
      url: location.pathname,
      isExact: true, // In v7, all matches are exact by default
    };

    return (
      <WrappedComponent
        {...props}
        match={match}
        location={location}
        history={history}
        navigate={navigate}
        params={params}
      />
    );
  }
  // Copy static properties
  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || "Component";
  WithRouterWrapper.displayName = `withRouter(${wrappedName})`;
  WithRouterWrapper.WrappedComponent = WrappedComponent;

  return WithRouterWrapper;
}
